import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";

/**
 * Ayuda de la Bandeja de correos. Antes, el «?» con globito y los tooltips de
 * cada botón y ficha. Los datos que vivían en tooltips (a quién llegó, la
 * razón de la IA, el estado de los casos por IMEI) ahora se ven en la fila.
 */
export function ayudaBandeja({
  isAdmin,
  esDueno,
  triageActivo,
}: {
  isAdmin: boolean;
  esDueno: boolean;
  triageActivo: boolean;
}): ContenidoAyuda {
  const si = (cond: boolean, items: ItemAyuda[]) => (cond ? items : []);
  return {
    manual: "correo-entrada",
    intro: (
      <>
        <p>
          Los correos que llegaron a <code>casos@arknex.net</code> y no se pudieron meter solos a
          un caso. Es compartida: la ven igual todos los de admin y soporte.
        </p>
        <p>
          Un correo entra solo a su caso, sin pasar por aquí, si su asunto trae el TE de un caso, o
          un IMEI que está en un solo caso abierto. Para contestarle a alguien, primero asigna su
          correo al caso y contesta desde la pestaña Correos del caso.
        </p>
      </>
    ),
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Revisar mi correo",
            text: "Abre tu buzón del correo empresarial para ver los títulos sin marcarlos como leídos, y traer a ArkTickets los que sirvan.",
          },
          { label: "↻ Refrescar", text: "Vuelve a cargar la bandeja." },
          {
            label: "En tu buzón hay N que podrían servir",
            text: "Sale solo cuando la revisión de tu buzón encontró correos que parecen de soporte. Tócalo para verlos.",
          },
          { label: "Tocar un correo (▼ ver)", text: "Lo abre ahí mismo con su texto completo y sus acciones; «▲ ocultar» lo cierra." },
          { label: "Texto / HTML", text: "Cambia entre el texto simple del correo y cómo se ve con su formato original." },
          {
            label: "Ver los descartados / ← Volver a los pendientes",
            text: "Cambia entre la bandeja y los correos que alguien descartó.",
          },
          {
            label: "Asignar a caso",
            text: "Abre un buscador de casos (por cliente, folio, TE, IMEI…) para mandar el correo al hilo del caso que elijas.",
          },
          {
            label: "Fichas «IMEI en caso»",
            text: "El asunto trae un IMEI que está en esos casos: toca uno y el correo se asigna a ese caso al instante, sin preguntar.",
          },
          {
            label: "📋 Crear caso desde este correo",
            text: "Sale cuando el correo es un formulario reconocido. Abre Nuevo caso con sus datos ya llenados; al guardarlo, el correo queda asignado al caso nuevo.",
          },
          {
            label: "↻ Re-detectar formato",
            text: "Vuelve a buscar en el correo un formulario reconocido. Sirve si llegó antes de que el sistema conociera ese formato.",
          },
          ...si(triageActivo, [
            {
              label: "✨ Revisar con IA / ↻ Revisar otra vez",
              text: "La IA lee el correo y decide si es un caso nuevo o si pertenece a un caso que ya existe. Si está muy segura y el TE o un IMEI del correo lo confirman, lo asigna sola.",
            },
          ]),
          {
            label: "✨ Crear caso (IA)",
            text: "Sale cuando la IA decidió que es un caso nuevo. Abre Nuevo caso con lo que leyó del correo; tú revisas y confirmas antes de crearlo.",
          },
          {
            label: "✨ Asignar a TE…",
            text: "Sale cuando la IA cree que el correo es de ese caso. Tócalo para asignarlo a su hilo.",
          },
          ...si(isAdmin, [
            {
              label: "Descartar",
              text: "Saca el correo de la bandeja (te pide confirmar). No se borra: queda en «Ver los descartados».",
            },
            {
              label: "↩ Devolver a la bandeja",
              text: "En los descartados: regresa el correo a la bandeja para poder asignarlo.",
            },
          ]),
          ...si(esDueno, [
            {
              label: "🗑 Borrar definitivamente",
              text: "En los descartados: lo borra de la base, con todo y cuerpo. No se deshace; solo queda el registro de quién lo borró.",
            },
          ]),
        ],
      },
      {
        titulo: "Qué significa cada ficha",
        items: [
          { label: "↘ IN", text: "Correo que llegó (entrante)." },
          {
            label: "→ dirección",
            text: "A qué direcciones llegó el correo, tal como venían en el «Para».",
          },
          {
            label: "📎 N adjuntos",
            text: "Cuántos archivos traía el correo. Los archivos no se guardan con los correos de la bandeja: si los necesitas, búscalos en el correo original.",
          },
          {
            label: "📋 garantia (u otro formulario)",
            text: "El correo es un formulario reconocido (por ejemplo, el de garantías): ábrelo para crear el caso con los datos ya llenados.",
          },
          {
            label: "✨ Caso nuevo / Pertenece a TE… / Sin acción",
            text: "Lo que decidió la IA al revisar el correo. Debajo del asunto va su razón; al abrir el correo se ve también qué tan segura está (alta, media o baja).",
          },
          {
            label: "Ficha de caso en gris · Cerrado",
            text: "En «IMEI en caso»: ese caso ya está cerrado. Las fichas en color son de casos abiertos; cada una dice el estado del caso.",
          },
        ],
      },
    ],
  };
}
